import { useAuth, useInboxConversation } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "../../../../components/ErrorBoundary";
import { MessageBubbleProps, ReversePaginatedList, withKey } from "@tellescope/ui";
import { Spinner } from "@tellescope/design-system";
import { formatName } from "@tellescope/utils";
import { useInboxThreadSearchParams } from "../../hooks";
import { Fragment } from "react";
import { MessageBubbleWithSecureAvatar } from "@tellescope/messaging";

export interface InboxConversationMessageListProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxConversationMessageList = withKey(
  ({ threadId, conversationData }: InboxConversationMessageListProps) => {
    const { t } = useTranslation("inbox");
    const { currentUser } = useAuth();
    const [queryParams] = useInboxThreadSearchParams();
    const draftId = queryParams.draftId;

    const internalConversationData = useInboxConversation(threadId ?? "", { enabled: !conversationData });
    const { metadata, messages, scheduled, loadNextPage, hasNextPage, isLoading, markReadOrUnread } =
      conversationData ?? internalConversationData;

    const isUnread = currentUser.id ? !metadata?.readBy?.[currentUser.id] : false;

    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      );
    }

    if (!metadata) {
      <div className="flex items-center justify-center h-full">
        <p className="text-destructive">{t("There was an error loading this thread...")}</p>
      </div>;
    }

    if (messages.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center p-8 text-center">
          <div className="text-sm text-muted-foreground">{t("No messages in this conversation yet")}</div>
        </div>
      );
    }

    const allMessages = [...messages, ...scheduled];

    return (
      <ErrorBoundary>
        <ReversePaginatedList
          className="flex-1 overflow-y-auto p-4 space-y-4"
          onTopReached={() => {
            if (hasNextPage) {
              loadNextPage();
            }
          }}
          onBottomReached={() => {
            if (isUnread && !!currentUser) {
              markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
            }
          }}
          onFocusWithin={({ isAtBottom }) => {
            if (isAtBottom && isUnread && currentUser) {
              markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
            }
          }}
          onClickWithin={({ isAtBottom }) => {
            if (isAtBottom && isUnread && currentUser) {
              markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
            }
          }}
        >
          {allMessages.map((message, index) => {
            if (draftId && message.id === draftId) return <Fragment key="draft-being-edited" />;

            const sender: MessageBubbleProps["sender"] = message.sender
              ? {
                  id: message.sender.id,
                  type: "user",
                  name: formatName(message.sender),
                  avatar: message.sender.avatar,
                }
              : {
                  id: "",
                  type: "user",
                  name: t("Unknown"),
                  avatar: undefined,
                };

            return (
              <MessageBubbleWithSecureAvatar
                key={message.id || index}
                content={message.content}
                timestamp={message.isScheduled ? message.sendAt ?? message.timestamp : message.timestamp}
                isOutbound={message.isOutbound}
                sender={sender}
                status={
                  message.error
                    ? {
                        type: "error",
                        message: message.error,
                      }
                    : message.isSending
                    ? {
                        type: "sending",
                        message: t("Sending..."),
                      }
                    : message.isScheduled
                    ? { type: "scheduled" }
                    : undefined
                }
                chatAttachments={message.attachments}
                isHighlighted={!!(currentUser.id && message.mentions?.includes(currentUser.id))}
              />
            );
          })}
        </ReversePaginatedList>
      </ErrorBoundary>
    );
  },
  "threadId"
);
